// DOMLifecycleManager.js
// Enterprise-grade DOM element lifecycle management
// Handles proper cleanup of loading elements, overlays, and state management

/**
 * DOM Lifecycle Manager
 * Manages complete lifecycle of DOM elements to prevent interference
 * Ensures proper cleanup and state management
 */
export class DOMLifecycleManager {
    static _instance = null;
    
    /**
     * Singleton pattern for consistent DOM management
     */
    static getInstance() {
        if (!DOMLifecycleManager._instance) {
            DOMLifecycleManager._instance = new DOMLifecycleManager();
        }
        return DOMLifecycleManager._instance;
    }
    
    constructor() {
        if (DOMLifecycleManager._instance) {
            return DOMLifecycleManager._instance;
        }
        
        this.managedElements = new Map();
        this.sceneStates = new Map();
        this.setupGlobalCleanup();
        
        DOMLifecycleManager._instance = this;
    }
    
    /**
     * Prepare DOM for scene transition
     * @param {string} fromSceneType - Source scene type ('dom', 'game')
     * @param {string} toSceneType - Target scene type ('dom', 'game')
     * @param {string} sceneKey - Target scene key
     */
    prepareSceneTransition(fromSceneType, toSceneType, sceneKey) {
        console.log(`🔄 DOMLifecycleManager: Preparing transition ${fromSceneType} → ${toSceneType} (${sceneKey})`);
        
        if (toSceneType === 'game') {
            this._prepareForGameScene(sceneKey);
        } else if (toSceneType === 'dom') {
            this._prepareForDOMScene(sceneKey);
        }
    }
    
    /**
     * Complete scene transition cleanup
     * @param {string} sceneType - Scene type that just loaded
     * @param {string} sceneKey - Scene key that just loaded
     */
    completeSceneTransition(sceneType, sceneKey) {
        console.log(`✅ DOMLifecycleManager: Completing transition to ${sceneType} (${sceneKey})`);
        
        if (sceneType === 'game') {
            this._finalizeGameScene(sceneKey);
        } else if (sceneType === 'dom') {
            this._finalizeDOMScene(sceneKey);
        }
        
        this.sceneStates.set('current', { type: sceneType, key: sceneKey });
    }
    
    /**
     * Prepare DOM environment for game scenes
     * @private
     */
    _prepareForGameScene(sceneKey) {
        console.log('🎮 Preparing DOM for game scene...');
        
        // 1. Hide all persistent loading elements
        this._hideAllLoadingElements();
        
        // 2. Remove all DOM overlays including dashboard
        this._removeAllDOMOverlays();
        
        // 2b. Specifically remove dashboard overlay and all its contents
        const dashboardOverlay = document.getElementById('auth-menu-dom-overlay');
        if (dashboardOverlay) {
            dashboardOverlay.remove();
            console.log('🗑️ Removed dashboard overlay: auth-menu-dom-overlay');
        }
        
        // 2c. Force remove any remaining dashboard elements
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
            sidebar.remove();
            console.log('🗑️ Force removed sidebar');
        }
        
        const appContainer = document.querySelector('.app-container');
        if (appContainer) {
            appContainer.remove();
            console.log('🗑️ Force removed app-container');
        }
        
        // 3. Set game active state
        document.body.classList.add('game-active');
        document.body.classList.remove('authenticated-dashboard-active');
        
        // 4. Prepare game container (but don't override positioning yet)
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            gameContainer.classList.remove('authenticated-dashboard');
            // Don't set positioning here - let GameInitializationCoordinator handle it
        }
        
        // 5. Show top ribbon (PPT Hero dependency) 
        const topRibbon = document.getElementById('top-ribbon');
        if (topRibbon) {
            topRibbon.style.display = 'flex';
            console.log('✅ Top ribbon shown for game navigation');
        }
        
        // 6. Create thin top navigation for fullscreen games
        this._createThinTopNavigation();
        
        console.log('✅ DOM prepared for game scene');
    }
    
    /**
     * Prepare DOM environment for DOM scenes
     * @private
     */
    _prepareForDOMScene(sceneKey) {
        console.log('🖥️ Preparing DOM for DOM scene...');
        
        // 1. Remove thin top navigation (return to dashboard)
        this._removeThinTopNavigation();
        
        // 2. Remove game active state
        document.body.classList.remove('game-active');
        
        // 3. Hide game container
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            gameContainer.style.display = 'none';
            gameContainer.style.visibility = 'hidden';
        }
        
        // 4. Hide top ribbon
        const topRibbon = document.getElementById('top-ribbon');
        if (topRibbon) {
            topRibbon.style.display = 'none';
        }
        
        // 4. Clean up any game-specific modifications
        this._cleanupGameModifications();
        
        console.log('✅ DOM prepared for DOM scene');
    }
    
    /**
     * Hide all persistent loading elements
     * @private
     */
    _hideAllLoadingElements() {
        const loadingSelectors = [
            '#loadingStatus',
            '#preloader',
            '.status-text',
            '.loading-state',
            '.desktop-loading',
            '.signin-loading',
            '.mobile-loading'
        ];
        
        loadingSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                element.style.display = 'none';
                console.log(`🙈 Hidden loading element: ${selector}`);
            });
        });
    }
    
    /**
     * Remove all DOM overlays
     * @private
     */
    _removeAllDOMOverlays() {
        // CRITICAL: Specifically remove dashboard overlay first
        const dashboardOverlay = document.getElementById('auth-menu-dom-overlay');
        if (dashboardOverlay) {
            dashboardOverlay.remove();
            console.log('🗑️ Removed dashboard overlay: auth-menu-dom-overlay');
        }
        
        // CRITICAL: Add CSS to force hide any remaining navigation elements
        const hideNavCSS = document.createElement('style');
        hideNavCSS.id = 'force-hide-navigation';
        hideNavCSS.textContent = `
            /* Force hide all navigation elements during game scenes */
            .sidebar, .nav-menu, .nav-section, .nav-item, 
            .sidebar-header, .sidebar-footer, .app-container,
            aside, nav {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                width: 0 !important;
                height: 0 !important;
                overflow: hidden !important;
                z-index: -9999 !important;
            }
            
            /* Ensure game container uses full width */
            #game-container {
                left: 0 !important;
                width: 100vw !important;
                margin-left: 0 !important;
            }
        `;
        document.head.appendChild(hideNavCSS);
        console.log('🚫 Added CSS to force hide navigation elements');
        
        const overlaySelectors = [
            '[id*="overlay"]:not(.notification-container)',
            '[id*="dom-overlay"]',
            '.overlay:not(.notification-container)'
        ];
        
        overlaySelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                if (element.id !== 'auth-menu-dom-overlay') { // Don't double-remove
                    element.remove();
                    console.log(`🗑️ Removed DOM overlay: ${element.id || element.className}`);
                }
            });
        });
    }
    
    /**
     * Finalize game scene setup
     * @private
     */
    _finalizeGameScene(sceneKey) {
        console.log('🎮 Finalizing game scene setup...');
        
        // Ensure proper game container visibility after Phaser initialization
        setTimeout(() => {
            const gameContainer = document.getElementById('game-container');
            if (gameContainer) {
                const computedStyle = getComputedStyle(gameContainer);
                
                if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
                    console.log('🔧 Game container not visible, applying fallback visibility');
                    gameContainer.style.display = 'block';
                    gameContainer.style.visibility = 'visible';
                    gameContainer.style.opacity = '1';
                }
            }
        }, 2000); // After game initialization completes
    }
    
    /**
     * Finalize DOM scene setup
     * @private
     */
    _finalizeDOMScene(sceneKey) {
        console.log('🖥️ Finalizing DOM scene setup...');
        
        // Ensure authenticated dashboard state if needed
        if (sceneKey === 'AuthenticatedMenuSceneDOM') {
            document.body.classList.add('authenticated-dashboard-active');
        }
        
        // Clean up any residual game elements
        this._cleanupGameModifications();
    }
    
    /**
     * Clean up game-specific modifications
     * @private
     */
    _cleanupGameModifications() {
        // Reset body background
        document.body.style.background = '';
        document.body.style.overflow = '';
        
        // Clean up any game-specific CSS classes
        document.body.classList.remove('game-active');
        
        // CRITICAL: Remove the CSS that was added to hide navigation during game
        const hideNavCSS = document.getElementById('force-hide-navigation');
        if (hideNavCSS) {
            hideNavCSS.remove();
            console.log('🧹 Removed force-hide-navigation CSS - navigation should now be visible');
        } else {
            console.log('⚠️ force-hide-navigation CSS not found - may have already been removed');
        }
        
        // Reset any modified elements
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            gameContainer.style.position = '';
            gameContainer.style.top = '';
            gameContainer.style.left = '';
            gameContainer.style.width = '';
            gameContainer.style.height = '';
            gameContainer.style.zIndex = '';
            gameContainer.style.background = '';
        }
    }
    
    /**
     * Setup global cleanup handlers
     * @private
     */
    setupGlobalCleanup() {
        // Clean up on page unload
        window.addEventListener('beforeunload', () => {
            this._cleanupAll();
        });
        
        // Clean up on visibility change
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this._validateDOMState();
            }
        });
    }
    
    /**
     * Validate DOM state is consistent
     * @private
     */
    _validateDOMState() {
        const currentState = this.sceneStates.get('current');
        if (!currentState) return;
        
        if (currentState.type === 'game') {
            // Ensure game container is visible
            const gameContainer = document.getElementById('game-container');
            if (gameContainer && getComputedStyle(gameContainer).display === 'none') {
                console.warn('🔧 Game container hidden unexpectedly, restoring visibility');
                gameContainer.style.display = 'block';
                gameContainer.style.visibility = 'visible';
            }
        }
    }
    
    /**
     * Clean up all managed elements
     * @private
     */
    _cleanupAll() {
        console.log('🧹 DOMLifecycleManager: Cleaning up all managed elements');
        
        this.managedElements.clear();
        this.sceneStates.clear();
        
        // Remove game active state
        document.body.classList.remove('game-active');
    }
    
    /**
     * Get current scene state
     * @returns {Object} - Current scene state
     */
    getCurrentSceneState() {
        return this.sceneStates.get('current') || { type: 'unknown', key: 'unknown' };
    }
    
    /**
     * Check if currently in game scene
     * @returns {boolean} - Whether currently in game scene
     */
    isInGameScene() {
        const currentState = this.getCurrentSceneState();
        return currentState.type === 'game';
    }
    
    /**
     * Emergency cleanup (for debugging)
     */
    emergencyCleanup() {
        console.log('🚨 DOMLifecycleManager: Emergency cleanup');
        
        this._hideAllLoadingElements();
        this._removeAllDOMOverlays();
        this._cleanupGameModifications();
        
        // Force game container visibility if in game scene
        if (this.isInGameScene()) {
            const gameContainer = document.getElementById('game-container');
            if (gameContainer) {
                gameContainer.style.display = 'block';
                gameContainer.style.visibility = 'visible';
                gameContainer.style.opacity = '1';
            }
        }
    }
    
    /**
     * Create thin top navigation for fullscreen games
     * @private
     */
    _createThinTopNavigation() {
        // Remove existing thin navigation
        const existing = document.getElementById('thin-top-nav');
        if (existing) existing.remove();
        
        const thinNav = document.createElement('div');
        thinNav.id = 'thin-top-nav';
        thinNav.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 50px;
            background: linear-gradient(90deg, #1a252f 0%, #2d3748 100%);
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 20px;
            z-index: 1000;
            font-family: 'Segoe UI', sans-serif;
            color: white;
            box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        `;
        
        thinNav.innerHTML = `
            <div style="display: flex; align-items: center; gap: 15px;">
                <span style="color: #00d4ff; font-weight: bold; font-size: 16px;">DeckOps.ai</span>
                <span style="color: rgba(255,255,255,0.7); font-size: 14px;">Executive Platform</span>
            </div>
            <button id="thin-nav-main-menu" style="
                background: #00d4ff; color: white; border: none; 
                padding: 8px 16px; border-radius: 4px; cursor: pointer;
                font-size: 14px; font-weight: 500;
            ">🏠 Main Menu</button>
        `;
        
        document.body.appendChild(thinNav);
        
        // Bind main menu navigation
        const mainMenuBtn = document.getElementById('thin-nav-main-menu');
        if (mainMenuBtn) {
            mainMenuBtn.addEventListener('click', () => {
                console.log('🏠 Thin nav: Main Menu clicked');
                // Use existing ribbon button logic
                document.getElementById('ribbon-mainmenu-btn')?.click();
            });
        }
        
        console.log('🎗️ Created thin top navigation for fullscreen game');
    }
    
    /**
     * Remove thin top navigation when returning to dashboard
     * @private
     */
    _removeThinTopNavigation() {
        const thinNav = document.getElementById('thin-top-nav');
        if (thinNav) {
            thinNav.remove();
            console.log('🗑️ Removed thin top navigation');
        }
        
        // Remove navigation hiding CSS
        const hideCSS = document.getElementById('force-hide-navigation');
        if (hideCSS) {
            hideCSS.remove();
            console.log('🗑️ Removed navigation hiding CSS');
        }
    }
}

// Export singleton instance
export const domLifecycleManager = DOMLifecycleManager.getInstance();

// Make globally available for debugging
if (typeof window !== 'undefined') {
    window.domLifecycleManager = domLifecycleManager;
    window.emergencyDOMCleanup = () => domLifecycleManager.emergencyCleanup();
}

export default domLifecycleManager;
